// src/components/ui/FloatingXP.jsx - ENHANCED VERSION
import React, { useState, useEffect } from 'react';
import { Zap, Star, Trophy, Award } from 'lucide-react';

const FloatingXP = ({
  xp,
  show,
  onComplete,
  position = { x: 0, y: 0 },
  type = 'xp', // 'xp', 'streak', 'achievement', 'bonus'
  customIcon = null,
  customColor = null
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationStage, setAnimationStage] = useState('initial');

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      setAnimationStage('entering');

      // Sequence of animation stages
      const timers = [
        setTimeout(() => setAnimationStage('floating'), 100),
        setTimeout(() => setAnimationStage('fading'), 1500),
        setTimeout(() => {
          setIsVisible(false);
          setAnimationStage('initial');
          if (onComplete) onComplete();
        }, 2500)
      ];

      return () => timers.forEach(clearTimeout);
    }
  }, [show, onComplete]);

  if (!isVisible) return null;

  // Type-specific configurations
  const typeConfig = {
    xp: {
      icon: <Zap className="w-5 h-5" />,
      color: 'from-yellow-400 to-orange-500',
      textColor: 'text-yellow-100',
      label: `+${xp} XP`,
      shadow: 'shadow-yellow-500/50'
    },
    streak: {
      icon: <Star className="w-5 h-5" />,
      color: 'from-orange-400 to-red-500',
      textColor: 'text-orange-100',
      label: `${xp} Day Streak!`,
      shadow: 'shadow-orange-500/50'
    },
    achievement: {
      icon: <Trophy className="w-5 h-5" />,
      color: 'from-purple-400 to-purple-600',
      textColor: 'text-purple-100',
      label: 'Achievement Unlocked!',
      shadow: 'shadow-purple-500/50'
    },
    bonus: {
      icon: <Award className="w-5 h-5" />,
      color: 'from-green-400 to-emerald-500',
      textColor: 'text-green-100',
      label: `+${xp} Bonus!`,
      shadow: 'shadow-green-500/50'
    }
  };

  const config = typeConfig[type];
  const finalIcon = customIcon || config.icon;
  const finalColor = customColor || config.color;

  // Animation classes based on stage
  const getAnimationClasses = () => {
    switch (animationStage) {
      case 'entering':
        return 'animate-bounce scale-0 opacity-0';
      case 'floating':
        return 'animate-pulse scale-100 opacity-100 transform translate-y-0';
      case 'fading':
        return 'scale-100 opacity-0 transform -translate-y-8';
      default:
        return 'scale-0 opacity-0';
    }
  };

  return (
    <div
      className="fixed pointer-events-none z-50"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -50%)'
      }}
    >
      <div
        className={`
          inline-flex items-center gap-2 px-4 py-2 rounded-full
          bg-gradient-to-r ${finalColor}
          ${config.shadow} shadow-lg
          ${config.textColor}
          font-bold text-sm
          border border-white/20
          backdrop-blur-sm
          transition-all duration-500 ease-out
          ${getAnimationClasses()}
        `}
      >
        {finalIcon}
        <span>{config.label}</span>

        {/* Sparkle effects */}
        <div className="absolute -inset-1 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`
                absolute w-1 h-1 bg-white rounded-full
                animate-ping
              `}
              style={{
                left: `${20 + (i * 15)}%`,
                top: `${10 + (i % 2 * 80)}%`,
                animationDelay: `${i * 200}ms`,
                animationDuration: '1s'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Hook for managing multiple floating XP animations
export const useFloatingXP = () => {
  const [notifications, setNotifications] = useState([]);

  const showXP = (xp, position, type = 'xp', customIcon = null, customColor = null) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      xp,
      position,
      type,
      customIcon,
      customColor,
      show: true
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };

  const FloatingXPRenderer = () => (
    <>
      {notifications.map(notification => (
        <FloatingXP
          key={notification.id}
          {...notification}
          onComplete={() => {
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
          }}
        />
      ))}
    </>
  );

  return { showXP, FloatingXPRenderer };
};

export default FloatingXP;